/** The Doop Agent pane of /settings: model accounts, Claude on the desktop,
 *  and the image model. Provider and model names stay untranslated. */
export const models = {
  /* shared by every plan row */
  activeConnected: 'Active · Connected',
  connected: 'Connected',
  notConnected: 'Not connected',
  connectedAs: 'Connected as',
  plan: 'Plan',
  connect: 'Connect',
  useInstead: 'Use instead',
  disconnect: 'Disconnect',
  model: 'Model',
  reasoningEffort: 'Reasoning effort',
  failedRetry: 'That did not work — try again',

  /* reasoning effort labels (the ids sent to the server stay as they are) */
  effortLow: 'Low',
  effortMedium: 'Medium',
  effortHigh: 'High',
  effortXhigh: 'Extra high',
  effortMax: 'Max',
  effortLowBlurb: 'Fastest and cheapest — fine for small, mechanical edits',
  effortMediumBlurb: 'The everyday setting. A good default for design work',
  effortHighBlurb: 'More thinking per turn — for layouts that have to be right',
  effortXhighBlurb: 'The most thinking, and the slowest and priciest turns',
  effortServerDefault: 'The server’s default',

  /* ModelAccount */
  chatgptSwitchFailed: 'ChatGPT connected, but switching providers failed. Retry the switch without signing in again.',
  signInIncomplete: 'That sign-in did not complete',
  andMore: ' and more',
  pickModel: 'Pick a model',
  visionNote: 'Text-only: this model can’t see screenshots, so designs aren’t visually reviewed.',
  noVisualReview: 'no visual review',
  rotateKey: 'Rotate key',
  keyStoredNote: 'The key is stored on the server and never shown again.',
  saving: 'Saving…',
  saveKey: 'Save key',
  codexPlan: 'Codex Plan',
  codexLede: 'Route OpenAI models through your ChatGPT subscription',
  retrySwitch: 'Retry switch',
  deviceStep1Before: 'One-time setup: turn on ',
  deviceStep1Bold: 'device code authorization',
  deviceStep1After: ' in ChatGPT → Settings → Security. (On a workspace account an admin has to allow it.)',
  deviceStep2Before: 'Open',
  deviceStep2After: 'and enter this code:',
  waitingApproval: 'Waiting for approval…',
  openVerificationPage: 'Open verification page',
  useBrowserSignIn: 'Device codes not available on your account? Use the browser sign-in instead',
  waitingOtherTab: 'Waiting for you to approve it in the other tab…',
  reopenSignIn: 'Reopen sign-in',
  pasteRedirectInstead: 'Paste the redirect URL instead',
  pasteStep1: 'Approve the connection in the tab that just opened.',
  pasteStep2Before: 'You will land on a ',
  pasteStep2Middle: ' page that ',
  pasteStep2Bold: 'fails to load',
  pasteStep2After: ' — that is expected.',
  pasteStep3: "Copy that page's full address and paste it below.",
  connecting: 'Connecting…',
  finishConnecting: 'Finish connecting',
  opening: 'Opening…',
  openaiKeyTitle: 'OpenAI API key',
  openaiKeyLede: 'Pay per token on your own OpenAI account — no ChatGPT subscription involved',
  openrouterKeyTitle: 'OpenRouter API key',
  openrouterKeyLede:
    'One key for Kimi, Qwen, GLM, DeepSeek, MiniMax, Gemini and more — pay per token on your OpenRouter account',
  geminiKeyTitle: 'Gemini API key',
  geminiKeyLede: 'Google’s Gemini models, plus Nano Banana image generation — pay per token on your Google AI account',
  claudeKeyTitle: 'Claude API key',
  claudeKeyLede: 'Pay per token on your Anthropic account. Runs on Doop’s server.',

  /* LocalClaude */
  cliSourceChosen: ', chosen by you.',
  cliSourceShell: ', the one your terminal runs.',
  cliSourceFound: ', the newest install found.',
  claudePlan: 'Claude Plan',
  claudePlanDesktopAria: 'Claude Plan — desktop app',
  desktopApp: 'Desktop app',
  claudePlanLede: 'Use your Claude subscription locally.',
  waitingDesktop: 'Waiting for a connected desktop.',
  useInDesktop: 'Use this plan in the Doop desktop app.',
  downloadDesktop: 'Download desktop app',
  claudeDefaultModel: 'Claude Code default',
  claudeFableBlurb: 'The newest Fable your Claude Code knows — for the hardest, longest tasks.',
  claudeOpusBlurb: 'The newest Opus — for complex layouts and demanding design or coding work.',
  claudeSonnetBlurb: 'The newest Sonnet — a balanced default for everyday design work.',
  claudeHaikuBlurb: 'The newest Haiku — for quick edits and straightforward tasks.',
  claudeDefaultBlurb: 'Whatever model your Claude Code is set to.',
  claudeEffortLowBlurb: 'Fastest — fine for small, mechanical edits',
  claudeEffortMediumBlurb: 'A good everyday setting for design work',
  claudeEffortHighBlurb: 'More thinking per turn — for layouts that have to be right',
  claudeEffortXhighBlurb: 'Deeper still; slower turns',
  claudeEffortMaxBlurb: 'All the thinking it can do, and the slowest turns',
  pinnedBlurb: 'Pinned to this exact model id; the aliases follow new releases on their own.',
  installToConnect: 'Install Claude Code to connect.',
  signInToConnect: 'Sign in to Claude Code to connect.',
  claudeModelAria: 'Claude model',
  installing: 'Installing…',
  installClaude: 'Install Claude Code',
  refresh: 'Refresh',
  signingIn: 'Signing in…',
  signIn: 'Sign in',
  stopTask: 'Stop task',
  effortDefault: 'Default',
  effortDefaultBlurb: 'Whatever your Claude Code is set to',
  pinAria: 'Pin a model id',
  pinPlaceholder: 'Pin a model id, e.g. claude-opus-5-20260901',
  pin: 'Pin',
  updateDoop: 'Update Doop to connect Claude.',
  outdatedBefore: 'Claude Code {version} at ',
  outdatedMiddle: ' is older than the {min} Doop needs. Run ',
  outdatedAfter: ' in a terminal, or {fix}, then Refresh.',
  outdatedChoose: 'choose another install',
  outdatedRemove: 'remove it so Doop finds your newer install',
  notFoundHere: 'Installed somewhere Doop did not look?',
  chosenMissingBefore: 'The Claude Code you chose at ',
  chosenMissingAfter: ' is gone.',
  usingVersion: 'Using Claude Code {version} at',
  using: 'Using Claude Code at',
  chooseCli: 'Choose…',
  useDefault: 'Use default',
  installGuide: 'Installation guide',
  working: 'Working…',
  lastTaskFailed: 'Last task failed',

  /* ImageModelCard */
  imageNeedsOpenai: 'Needs a ChatGPT subscription or OpenAI API key connected above (or a server key).',
  imageNeedsGemini: 'Needs a Gemini API key connected above (or a server key).',
  imageNeedsArk: 'Not configured on this server (its operator sets ARK_API_KEY).',
  imageGptBlurb: 'OpenAI’s image model — reliable text rendering and UI assets.',
  imageNanoBananaBlurb: 'Google’s image model — photoreal scenes, editing, 4K.',
  imageSeedreamProBlurb: 'ByteDance’s top image model — rich, painterly quality.',
  imageSeedreamLiteBlurb: 'Faster, cheaper Seedream tier.',
  imageModelTitle: 'Image model',
  imageModelDescription:
    'Which model the agent draws generated images with. It runs on the matching account above, or on this server’s key when you connected nothing.',
} as const
